import React, {useContext, useEffect} from "react";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../../../../containers/user-homepage";
import {UserHomepageComponentTypes, UserHomepageViewEvents, UserHomepageViewMode} from "../../../../../containers/user-homepage/models";
import UserHomepageUtils from "../../../../../containers/user-homepage/core/utils";
import {routeFunctions, routes} from "../../../../../../core/models/static/routes/app-routes";
import {openLinkInNewTab} from "../../../../../../core/services/utils/utils";
import {useCachedIndustrySchemaController} from "../../../../../hooks/use-industry";
import {NexusSearchSchemaEntryNames} from "../../../../../../core/constants/enums";
import ToastService from "../../../../../../core/services/toast";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {stringifyUrl} from "query-string";

const UserPlatformPreviewNavigation = () => {
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const {getSchemaForIndustry} = useCachedIndustrySchemaController()
    const operatorAccessLevels = useAccessLevels();


    /**
     * With each change in the controller and state:
     * - attaches an event listener that registers or unregisters edit mode entity clicks of the homepage.
     */
    useEffect(() => {
        switch (state.viewMode) {
            case UserHomepageViewMode.preview:
                registerPreviewNavigation();
                break;
            default:
                unregisterPreviewNavigation();
                break;
        }
        return () => {
            unregisterPreviewNavigation();
        }
    }, [state?.viewMode, state?.updatedData])

    /**
     * Unregisters any preview specific navigations of the application.
     */
    const unregisterPreviewNavigation = () => {
        controller.off(UserHomepageViewEvents.onComponentSearchSelected, onComponentSearchClicked);
        controller.off(UserHomepageViewEvents.onContentClicked, onContentClicked);
        controller.off(UserHomepageViewEvents.onProductTagSelected, onTagClicked);
    }

    /**
     * registers any preview specific navigations of the application.
     */
    const registerPreviewNavigation = () => {
        controller.on(UserHomepageViewEvents.onComponentSearchSelected, onComponentSearchClicked);
        controller.on(UserHomepageViewEvents.onContentClicked, onContentClicked);
        controller.on(UserHomepageViewEvents.onProductTagSelected, onTagClicked);
    }

    /**
     * Handles the "more" or "see all" buttons of each component in homepage preview mode.
     * @param {number} tabId
     * @param {number} componentId
     * @returns {Promise<void|undefined>}
     */
    const onComponentSearchClicked = async (tabId, componentId) => {
        const component = UserHomepageUtils.getSelection(state.updatedData, tabId, componentId);
        if (!component)
            return;
        switch (component.type?.id) {
            case UserHomepageComponentTypes.largeProduct:
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.timeBased:
                return navigateToLookupWithComponentSearchQuery(component);
            default:
                return;
        }
    }

    /**
     * Navigates the user to the lookup results view with the existing search query and search type of the given
     * component.
     * @param {UserHomepageComponent} component
     * @returns {Promise<void>}
     */
    const navigateToLookupWithComponentSearchQuery = async (component) => {
        if (!component.nexusSearchId)
            return;
        navigateToLookupResults({
            nexusSearchId: component.nexusSearchId,
        })
    }

    /**
     * Navigates the user to the proper view based on the components type.
     * @param tabId
     * @param componentId
     * @param contentId
     * @returns {Promise<void>}
     */
    const onContentClicked = async (tabId, componentId, contentId) => {
        const component = UserHomepageUtils.getSelection(state.updatedData, tabId, componentId);
        if (!component)
            return;
        const content = UserHomepageUtils.getSelection(state.updatedData, tabId, componentId, contentId);
        if (!content)
            return;
        switch (component.type?.id) {
            case UserHomepageComponentTypes.banner:
            case UserHomepageComponentTypes.slideShow: {
                return navigateToLookupResults({
                    tags: content?.tags,
                });
            }
            case UserHomepageComponentTypes.category: {
                return navigateToLookupResults({
                    category: content,
                });
            }
            case UserHomepageComponentTypes.largeProduct:
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.timeBased: {
                const url = routeFunctions.lookup.single(content.partNo);
                return openLinkInNewTab(window.location.origin + url);
            }
            default:
                return;
        }
    }

    /**
     * Navigates the user to the lookup results view with basic search engine.
     *
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     * @param {number} tagId
     * @returns {Promise<void>}
     */
    const onTagClicked = async (tabId, componentId, contentId, tagId) => {
        if (!tagId || Number.isNaN(tagId))
            return;
        const content = UserHomepageUtils.getSelection(state.updatedData, tabId, componentId, contentId);
        navigateToLookupResults({
            tags: content?.tags?.filter(t => t.id === tagId),
        })
    }

    /**
     * Navigates the user to the lookup view with the given tab and data.
     *
     * * the navigation is actually done by opening a new tab with the generated url.
     */
    const navigateToLookupResults = async ({tags, category, nexusSearchId}) => {
        let url;
        if (!operatorAccessLevels.packages.lookup.nexusSearch) {
            ToastService.error({
                title: "Access Denied",
                message: "You do not have access to view nexus search results, please contact your administrator.",
            });
            return;
        }
        if (nexusSearchId) {
            url = stringifyUrl({
                url: routes.lookup.search,
                query: {
                    searchToken: nexusSearchId,
                },
            })
        } else {
            const filters = [];
            let industryId;
            if (tags) {
                industryId = tags[0].industryId;
                const schema = await getSchemaForIndustry(industryId);
                if (schema) {
                    const entry = schema.entries.find(e => e.name === NexusSearchSchemaEntryNames.tag);
                    filters.push(...tags.map(t => ({
                        filterId: entry.id,
                        value: t.name,
                    })))
                } else {
                    ToastService.error({
                        title: "Error",
                        message: "Could not find the information related to the provided tag to navigate to search view",
                    });
                    return;
                }
            }
            if (category) {
                industryId = category.industryId;
                const schema = await getSchemaForIndustry(industryId);
                if (schema) {
                    const entry = schema.entries.find(e => e.name === NexusSearchSchemaEntryNames.category);
                    filters.push({
                        filterId: entry.id,
                        value: category.title,
                    })
                } else {
                    ToastService.error({
                        title: "Error",
                        message: "Could not find the information related to the provided tag to navigate to search view",
                    });
                    return;
                }
            }
            url = stringifyUrl({
                url: routes.lookup.search,
                query: {
                    industryId: industryId,
                    filters: JSON.stringify(filters),
                },
            })
        }
        openLinkInNewTab(window.location.origin + url);
    }

    return (<></>);
}
export default UserPlatformPreviewNavigation;
